/// <reference types="../beacons.d.ts" />
import * as mock from "@myvp/shared/src/beacons/beacons.mock"
import * as real from "@myvp/shared/src/beacons/beacons.service"
import { selectService } from "@myvp/shared/src/functions/mock"
import { getDeviceDate } from "@myvp/shared/src/clock/get-device-date"
import { tabId } from "@myvp/shared/src/functions/request"
import { getDeviceProperties } from "@myvp/shared/src/functions/get-device-properties"
import { uuid } from "@myvp/shared/src/functions/uuid"
import { Role } from "@myvp/shared/src/gql/graphql"
import { getRum } from "@myvp/shared/src/cloudwatch-RUM"

const service =
  process.env.REACT_APP_release === "true"
    ? real
    : selectService({ mock, real })

export const logger = async (
  activityName: string,
  properties?: { [key: string]: string | undefined },
  timingData?: {
    serverDurationMillis: number
    clientDurationMillis: number | undefined
  }
) => {
  if (process.env.REACT_APP_allow_beacons !== "true") {
    return
  }

  const params = new URLSearchParams(window.location.search)

  const studyId = params.get("studyId")
  const role = params.get("role") as Role
  const participantRelatedUserId = params.get("relatedUserId")

  let studyRoleContext = undefined
  if (studyId && role && participantRelatedUserId) {
    studyRoleContext = {
      studyId,
      role,
      participantRelatedUserId,
    }
  }

  const deviceProperties = getDeviceProperties()
  const memoryStats =
    performance && performance.memory
      ? {
          used_memory: performance.memory.usedJSHeapSize,
          available_memory:
            performance.memory.totalJSHeapSize -
            performance.memory.usedJSHeapSize,
          total_memory: performance.memory.totalJSHeapSize,
        }
      : undefined
  const memoryString = memoryStats
    ? `Available memory: ${memoryStats.available_memory}; used memory: ${memoryStats.used_memory}; total memory: ${memoryStats.total_memory}`
    : ""
  const isTouchEnabled =
    "ontouchstart" in window || navigator.maxTouchPoints > 0

  const extendedProperties = {
    ...properties,
    isTouchEnabled: isTouchEnabled ? "true" : "false",
  }

  try {
    const beacons = [
      {
        id: uuid(),
        properties: Object.entries(extendedProperties).map((entry) => ({
          key: entry[0],
          value: entry[1],
        })),
        activity: activityName,
        activityAt: getDeviceDate().toISOString(),
        timingData,
        deviceProperties: {
          appVersion: deviceProperties.app_version ?? "",
          deviceTimeZone: deviceProperties.timezone,
          deviceTimeZoneOffset: deviceProperties.timezoneOffset,
          displayDimensionsUnit: "px",
          displayHeight: deviceProperties.currentHeight,
          displayWidth: deviceProperties.currentWidth,
          platform: deviceProperties.platform,
          userAgent: deviceProperties.user_agent,
        },
        systemProperties: { memoryInfo: memoryString, pid: tabId },
        studyRoleContext,
        userAuthId: localStorage.getItem("myvp-sub"),
      },
    ]

    return await Promise.all([
      service.saveBeacons({ beacons }),
      new Promise(async (resolve) => {
        const rumUtils = await getRum()
        if (rumUtils) {
          beacons.forEach((beacon) => {
            rumUtils.logCustomEvent({ name: "beacon", body: beacon })
          })
        }
        resolve({})
      }),
    ])
  } catch (e) {
    // oxlint-disable-next-line no-console
    console.error(e)
  }
}

const timerMap = new Map<string, number>()
const elapsedTimeMap = new Map<string, number>()

export const startTimer = (activityName: string) => {
  const startTime = getDeviceDate().getTime()
  timerMap.set(activityName, startTime)
}

export const endTimer = (
  activityName: string,
  properties?: { [key: string]: string | undefined }
) => {
  const endTime = getDeviceDate().getTime()
  const startTime = timerMap.get(activityName)

  if (startTime === undefined) {
    return
  }

  const elapsedTime = endTime - startTime
  elapsedTimeMap.set(activityName, elapsedTime)
  timerMap.delete(activityName)

  const baseActivity = activityName.replace(/(_CLIENT|_SERVER)$/, "")
  const clientKey = `${baseActivity}_CLIENT`
  const serverKey = `${baseActivity}_SERVER`

  if (elapsedTimeMap.has(clientKey) && elapsedTimeMap.has(serverKey)) {
    logger(baseActivity, properties, {
      clientDurationMillis: elapsedTimeMap.get(clientKey),
      serverDurationMillis: elapsedTimeMap.get(serverKey) ?? 0,
    })
    elapsedTimeMap.delete(clientKey)
    elapsedTimeMap.delete(serverKey)
  }
}

export const endTimerWithoutClient = (
  activityName: string,
  properties?: { [key: string]: string | undefined }
) => {
  const endTime = getDeviceDate().getTime()
  const startTime = timerMap.get(activityName)
  if (startTime === undefined) {
    return
  }
  timerMap.delete(activityName)

  const elapsedTime = endTime - startTime
  const baseActivity = activityName.replace(/_SERVER$/, "")

  logger(baseActivity, properties, {
    clientDurationMillis: undefined,
    serverDurationMillis: elapsedTime,
  })
}
